// @ts-strict-ignore
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TableRowLink from "@dashboard/components/TableRowLink";
import { CountryFragment } from "@dashboard/graphql";
import { Checkbox, TableBody, TableCell } from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { DeleteIcon, IconButton, makeStyles } from "@saleor/macaw-ui";
import { Button, Text } from "@saleor/macaw-ui-next";
import clsx from "clsx";
import * as React from "react";
import { FormattedMessage } from "react-intl";

import { getStringOrPlaceholder } from "../../misc";
import { DashboardCard } from "../Card";
import { groupCountriesByStartingLetter } from "./utils";

interface CountryListProps {
  countries: CountryFragment[];
  disabled: boolean;
  emptyText: React.ReactNode;
  title: React.ReactNode;
  onCountryAssign: () => void;
  onCountryUnassign: (country: string) => void;
  onCountriesUnassign?: (codes: string[]) => void;
}

const useStyles = makeStyles(
  theme => ({
    iconCell: {
      "&:last-child": {
        paddingRight: theme.spacing(3),
        paddingLeft: 0,
      },
      width: `calc(48px + ${theme.spacing(4)})`,
    },
    checkCell: {
      width: 40,
      paddingLeft: theme.spacing(2),
      paddingRight: 0,
    },
    indicator: {
      color: theme.palette.text.disabled,
      display: "inline-block",
      left: 0,
      marginRight: theme.spacing(0.5),
      position: "absolute",
    },
    offsetCell: {
      "&:first-child": {
        paddingLeft: theme.spacing(3),
      },
      position: "relative",
    },
    pointer: {
      cursor: "pointer",
    },
    root: {
      "&:last-child": {
        paddingBottom: 0,
      },
      paddingTop: 0,
    },
    rotate: {
      transform: "rotate(180deg)",
    },
    textRight: {
      textAlign: "right",
    },
    toLeft: {
      "&:first-child": {
        paddingLeft: 0,
      },
    },
    wideColumn: {
      width: "100%",
    },
    // Bulk-action bar — a full-width row inside the table, shown when ≥1 item selected
    bulkBar: {
      backgroundColor: theme.palette.background.default,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    bulkBarCell: {
      padding: theme.spacing(0.5, 2),
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
    },
  }),
  { name: "CountryList" },
);

const CountryList = (props: CountryListProps) => {
  const {
    countries,
    disabled,
    emptyText,
    title,
    onCountryAssign,
    onCountryUnassign,
    onCountriesUnassign,
  } = props;
  const classes = useStyles(props);
  const [isCollapsed, setCollapseStatus] = React.useState(true);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());

  const toggleCollapse = () => {
    setCollapseStatus(prev => {
      if (!prev) setSelected(new Set());

      return !prev;
    });
  };

  function sortCountries(list: CountryFragment[]): CountryFragment[] {
    return [...list].sort((a, b) => a.country.localeCompare(b.country));
  }

  const sortedCountries = sortCountries(countries ?? []);
  const groupedCountries = groupCountriesByStartingLetter(sortedCountries);
  const hasCountriesToRender = sortedCountries.length > 0;

  const allCodes = sortedCountries.map(c => c.code);
  const allSelected = allCodes.length > 0 && allCodes.every(c => selected.has(c));
  const someSelected = selected.size > 0 && !allSelected;
  const supportsBulk = !!onCountriesUnassign;
  // Number of <td> columns in a regular row
  const colCount = supportsBulk ? 3 : 2;

  const toggleOne = (code: string) => {
    setSelected(prev => {
      const next = new Set(prev);

      if (next.has(code)) next.delete(code);
      else next.add(code);

      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(allCodes));
  };

  const handleBulkDelete = () => {
    const codes = Array.from(selected);

    if (onCountriesUnassign) {
      onCountriesUnassign(codes);
    } else {
      codes.forEach(code => onCountryUnassign(code));
    }

    setSelected(new Set());
  };

  return (
    <DashboardCard>
      {/* Header — only ever has ONE button so it never overflows its column */}
      <DashboardCard.Header>
        <DashboardCard.Title>{title}</DashboardCard.Title>
        <DashboardCard.Toolbar>
          <Button
            disabled={disabled}
            onClick={onCountryAssign}
            data-test-id="assign-country"
            variant="secondary"
          >
            <FormattedMessage id="zZCCqz" defaultMessage="Assign countries" description="button" />
          </Button>
        </DashboardCard.Toolbar>
      </DashboardCard.Header>

      <ResponsiveTable>
        <TableBody>
          {/* ── Collapse toggle row ── */}
          <TableRowLink className={classes.pointer} onClick={toggleCollapse}>
            <TableCell className={clsx(classes.wideColumn, classes.toLeft)}>
              <FormattedMessage
                id="62Ywh2"
                defaultMessage="{number} Countries"
                description="number of countries"
                values={{
                  number: getStringOrPlaceholder(countries?.length.toString()),
                }}
              />
            </TableCell>
            <TableCell className={clsx(classes.textRight, classes.iconCell)}>
              <IconButton variant="secondary">
                <ArrowDropDownIcon
                  data-test-id="countries-drop-down-icon"
                  className={clsx({ [classes.rotate]: !isCollapsed })}
                />
              </IconButton>
            </TableCell>
          </TableRowLink>

          {/* ── Bulk action bar — appears INSIDE the table when items are selected ── */}
          {!isCollapsed && supportsBulk && selected.size > 0 && (
            <TableRowLink className={classes.bulkBar}>
              <TableCell colSpan={colCount} className={classes.bulkBarCell}>
                <Text size={2} color="default2">
                  <FormattedMessage
                    id="NKPfmm"
                    defaultMessage="{count} selected"
                    values={{ count: selected.size }}
                  />
                </Text>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={handleBulkDelete}
                  disabled={disabled}
                  style={{ color: "#dc2626", borderColor: "#dc2626" }}
                >
                  <FormattedMessage id="9AvbaU" defaultMessage="Remove selected" />
                </Button>
              </TableCell>
            </TableRowLink>
          )}

          {/* ── Select-all row ── */}
          {!isCollapsed && hasCountriesToRender && supportsBulk && (
            <TableRowLink className="select-all-row" hover={false}>
              <TableCell className={classes.checkCell}>
                <Checkbox
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={disabled}
                  size="small"
                />
              </TableCell>
              <TableCell colSpan={2} style={{ paddingLeft: 8 }}>
                <Text size={2} color="default2">
                  {allSelected ? (
                    <FormattedMessage id="Spd88H" defaultMessage="Deselect all" />
                  ) : (
                    <FormattedMessage
                      id="BcUkR0"
                      defaultMessage="Select all {count}"
                      values={{ count: allCodes.length }}
                    />
                  )}
                </Text>
              </TableCell>
            </TableRowLink>
          )}

          {/* ── Country rows ── */}
          {!isCollapsed && hasCountriesToRender
            ? Object.keys(groupedCountries).map(letter => {
                const letterCountries = groupedCountries[letter];

                return letterCountries.map((country, countryIndex) => (
                  <TableRowLink key={country ? country.code : "skeleton"}>
                    {supportsBulk && (
                      <TableCell className={classes.checkCell}>
                        <Checkbox
                          checked={selected.has(country.code)}
                          onChange={() => toggleOne(country.code)}
                          disabled={!country || disabled}
                          size="small"
                          onClick={e => e.stopPropagation()}
                        />
                      </TableCell>
                    )}
                    <TableCell
                      className={supportsBulk ? undefined : classes.offsetCell}
                      style={{ paddingLeft: supportsBulk ? 8 : undefined, width: "100%" }}
                    >
                      {countryIndex === 0 && (
                        <Text color="default2" display="inline-block" style={{ marginRight: 6 }}>
                          {country.country[0]}
                        </Text>
                      )}
                      {country.country}
                    </TableCell>
                    <TableCell className={clsx(classes.textRight, classes.iconCell)}>
                      <IconButton
                        data-test-id="delete-icon"
                        variant="secondary"
                        disabled={!country || disabled}
                        onClick={() => onCountryUnassign(country.code)}
                      >
                        <DeleteIcon
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                        />
                      </IconButton>
                    </TableCell>
                  </TableRowLink>
                ));
              })
            : !isCollapsed && (
                <TableRowLink>
                  <TableCell className={classes.toLeft} colSpan={colCount}>
                    {emptyText}
                  </TableCell>
                </TableRowLink>
              )}
        </TableBody>
      </ResponsiveTable>
    </DashboardCard>
  );
};

export default CountryList;
